import Colors from '@constants/Colors';

import { AssigneeType, JobOfferAssignee, LegacyAssignee, OrganizationUserDetails, ProDetails } from '../jobOffer.types';

/**
 * Converts new polymorphic assignee structure to legacy format for form compatibility
 * @param assignees - Array of JobOfferAssignee objects
 * @returns Array of LegacyAssignee objects
 */
export const convertAssigneesToLegacyFormat = (assignees?: JobOfferAssignee[]): LegacyAssignee[] => {
    if (!assignees) return [];

    return assignees.map(assignee => {
        if (assignee.assigneeType === AssigneeType.OrganizationUser && assignee.assigneeDetails) {
            const details = assignee.assigneeDetails as OrganizationUserDetails;
            return {
                _id: details._id,
                firstName: details.firstName,
                lastName: details.lastName,
                email: details.email,
                role: details.role,
                assigneeType: assignee.assigneeType,
                color: details.color,
                file: details.file
            };
        } else if (assignee.assigneeType === AssigneeType.Pro && assignee.assigneeDetails) {
            const details = assignee.assigneeDetails as ProDetails;
            return {
                _id: details._id,
                firstName: details.bossFirstname || details.commercialName,
                lastName: details.bossLastname || '',
                email: details.email,
                role: details.role || 'PRO',
                assigneeType: assignee.assigneeType,
                color: Colors.PRIMARY,
                file: details.bossPhoto ? [{ _id: '', name: '', url: details.bossPhoto }] : []
            };
        }

        // Fallback for legacy format - this should not happen in practice
        return assignee as any;
    });
};

/**
 * Converts legacy assignee format to new polymorphic format
 * @param legacyAssignees - Array of legacy assignee objects
 * @returns Array of AssigneeInput objects for mutations
 */
export const convertLegacyToAssigneeInput = (legacyAssignees: LegacyAssignee[]) => {
    return legacyAssignees.map(assignee => ({
        _id: assignee._id,
        assigneeType: assignee.assigneeType || (
            assignee.role === 'COMPANY' || assignee.role === 'PRO' || assignee.role === 'FREELANCE'
                ? AssigneeType.Pro
                : AssigneeType.OrganizationUser
        ),
        role: assignee.role
    }));
};

/**
 * Gets display information from polymorphic assignee object
 * @param assignee - JobOfferAssignee object or legacy assignee
 * @returns Object with firstName, lastName, email, photo, and color
 */
export const getAssigneeDisplayInfo = (assignee: JobOfferAssignee | LegacyAssignee | any) => {
    // Check if it's the new polymorphic format
    if ('assigneeType' in assignee && 'assigneeDetails' in assignee) {
        const { assigneeType, assigneeDetails } = assignee as JobOfferAssignee;

        if (assigneeType === AssigneeType.OrganizationUser) {
            const details = assigneeDetails as OrganizationUserDetails;
            return {
                firstName: details.firstName,
                lastName: details.lastName,
                email: details.email,
                photo: details.file?.[0]?.url,
                color: details.color
            };
        } else if (assigneeType === AssigneeType.Pro) {
            const details = assigneeDetails as ProDetails;
            return {
                firstName: details.bossFirstname || details.commercialName,
                lastName: details.bossLastname || '',
                email: details.email,
                photo: details.bossPhoto,
                color: Colors.PRIMARY
            };
        }
    }

    // Legacy format fallback
    const legacyAssignee = assignee as LegacyAssignee;
    return {
        firstName: legacyAssignee.firstName || '',
        lastName: legacyAssignee.lastName || '',
        email: legacyAssignee.email || '',
        photo: legacyAssignee.file?.[0]?.url,
        color: legacyAssignee.color || Colors.PRIMARY
    };
};

/**
 * Gets role label for display
 * @param assignee - JobOfferAssignee object or legacy assignee
 * @returns Human-readable role label
 */
export const getAssigneeRoleLabel = (assignee: JobOfferAssignee | LegacyAssignee | any): string => {
    let role: string;

    // Check if it's new polymorphic format
    if ('assigneeType' in assignee && 'assigneeDetails' in assignee) {
        const { assigneeDetails } = assignee as JobOfferAssignee;
        role = (assigneeDetails as any).role;
    } else {
        // Legacy format
        role = (assignee as LegacyAssignee).role;
    }

    switch (role) {
        case 'ADMIN_PRO':
            return 'Admin';
        case 'MEMBER_PRO':
        case 'ACCOUNTANT_PRO':
            return 'Membre';
        case 'COMPANY':
        case 'PRO':
            return 'Super Administrateur';
        default:
            return 'Utilisateur';
    }
};
